/**
 * Phase 39 — "Right now" for a card on a tracker source: the draft rule
 * judged live on the items the card would judge today (each group's list of
 * the day, the pool's routes), debounced as the levers move. Zero writes;
 * nothing to show before the container is saved or on today's drops.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useQuery } from '@tanstack/react-query';
import type { DropGenSourcePreview, DropGenVerdictCard, DropGenVerdictSource } from '@trafficpulse/shared';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { api } from '@/services/api';

import type { DraftFlatRule } from '../../../../drop-generator.types';
import { dropGeneratorKeys } from '../../../../useDropGenerator';
import { flatRuleToTree } from '../../state/satellites/verdict-draft';

export interface SourcePreviewArgs {
  countryKey: string;
  containerId: string | null;
  card: DropGenVerdictCard;
  source: DropGenVerdictSource;
  rule: DraftFlatRule;
  minSampleSent: number;
}

export function useSourcePreview(args: SourcePreviewArgs) {
  const body = useDebouncedValue(
    {
      card: args.card,
      source: args.source,
      // A nested (API-set) rule is left out — the saved one would be judged; the preview says nothing then.
      tree: args.rule.advanced ? undefined : flatRuleToTree(args.rule),
      minSampleSent: args.minSampleSent,
    },
    500,
  );
  const enabled = args.containerId !== null && args.source.kind !== 'drops' && !args.rule.advanced;
  return useQuery({
    queryKey: [...dropGeneratorKeys.all, 'source-preview', args.countryKey, args.containerId, body] as const,
    queryFn: async () => {
      const res = await api.post<{ data: DropGenSourcePreview }>(`/drop-generator/verdicts/${args.countryKey}/containers/${args.containerId}/source-preview`, body);
      return res.data.data;
    },
    enabled,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}
